import { useTranslation } from "react-i18next"

import type { PluginEntry } from "@/api/plugins-api"

import { Building2, Plug, Settings2 } from "@/components/icons"
import { cn } from "@/lib/utils"

export type PluginsMenuPanelProps = {
  /** Per-chat master switch. */
  enablePlugins: boolean
  onPluginsChange: (enabled: boolean) => void
  /** Installed plugins offered on this surface. */
  installed: PluginEntry[]
  /** Ids the user switched off for this chat (exclusion list). */
  disabledPluginIds: string[]
  onPluginDisabledChange: (pluginId: string, disabled: boolean) => void
  /** Catalog state; the panel explains why the list is empty. */
  isPending: boolean
  isError: boolean
  storeEnabled: boolean
  /** Shown only when the /plugins page exists on this tenant. */
  onManage?: () => void
}

export type MenuToggleRowProps = {
  icon: React.ReactNode
  label: string
  description?: string
  badge?: string
  checked: boolean
  disabled?: boolean
  onToggle: (checked: boolean) => void
  testId?: string
}

/**
 * Plain-button toggle row. The panel is mounted both inside a Radix submenu
 * (wrapped in a stopPropagation container, like the trusted-sources panel, so
 * several plugins can be switched in one pass without the menu closing) and
 * inside the mobile drawer, so it deliberately does not use DropdownMenuItem.
 */
export function MenuToggleRow({
  icon,
  label,
  description,
  badge,
  checked,
  disabled,
  onToggle,
  testId,
}: MenuToggleRowProps) {
  return (
    <button
      aria-checked={checked}
      data-testid={testId}
      disabled={disabled}
      role="switch"
      title={description}
      type="button"
      className={cn(
        "hover:bg-accent flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm",
        checked && "text-accent-foreground dark:text-foreground",
        disabled && "cursor-not-allowed opacity-60"
      )}
      onClick={() => {
        if (disabled) return
        onToggle(!checked)
      }}
    >
      {icon}
      <span className="min-w-0 flex-1 truncate">{label}</span>
      {badge && (
        <span className="bg-primary/10 text-primary rounded-sm px-1 py-0.5 text-[10px] leading-none font-medium">
          {badge}
        </span>
      )}
      <span className="flex size-4 items-center justify-center">
        <span
          className={cn(
            "size-2 rounded-full",
            checked ? "bg-primary" : "border-muted-foreground/50 border"
          )}
        />
      </span>
    </button>
  )
}

/**
 * The "Plugins" submenu of the chat tools menu: a per-chat master switch, one
 * row per installed plugin, and a link to the /plugins page. Installation
 * itself stays on that page, where operations and permissions are reviewed.
 */
export function PluginsMenuPanel({
  enablePlugins,
  onPluginsChange,
  installed,
  disabledPluginIds,
  onPluginDisabledChange,
  isPending,
  isError,
  storeEnabled,
  onManage,
}: PluginsMenuPanelProps) {
  const { t } = useTranslation()
  const iconClassName = (enabled: boolean) =>
    cn("size-4", enabled ? "text-primary" : "text-muted-foreground")

  let status: string | null = null
  if (isPending) status = t("questioninput.plugins_loading", { defaultValue: "Loading plugins…" })
  else if (isError)
    status = t("questioninput.plugins_unavailable", {
      defaultValue: "Plugins are unavailable right now",
    })
  else if (!storeEnabled)
    status = t("questioninput.plugins_not_enabled", {
      defaultValue: "Plugins are not enabled for your company",
    })
  else if (installed.length === 0)
    status = t("questioninput.plugins_none_installed", { defaultValue: "No plugins installed" })

  return (
    <div className="py-1" data-testid="plugins-menu-panel">
      <MenuToggleRow
        checked={enablePlugins}
        icon={<Plug className={iconClassName(enablePlugins)} />}
        label={t("questioninput.plugins_use", { defaultValue: "Use plugins in this chat" })}
        testId="plugins-menu-master"
        onToggle={onPluginsChange}
      />
      <div className="bg-border my-1 h-px" role="separator" />
      {status ? (
        <p className="text-muted-foreground px-2 py-1.5 text-xs" data-testid="plugins-menu-status">
          {status}
        </p>
      ) : (
        installed.map((entry) => {
          const active = enablePlugins && !disabledPluginIds.includes(entry.id)
          return (
            <MenuToggleRow
              key={entry.id}
              checked={active}
              description={entry.description}
              disabled={!enablePlugins}
              label={entry.name}
              testId={`plugins-menu-item-${entry.id}`}
              badge={
                entry.managed
                  ? t("questioninput.plugins_company_badge", { defaultValue: "Company" })
                  : undefined
              }
              icon={
                entry.managed ? (
                  <Building2 className={iconClassName(active)} />
                ) : (
                  <Plug className={iconClassName(active)} />
                )
              }
              onToggle={(checked) => onPluginDisabledChange(entry.id, !checked)}
            />
          )
        })
      )}
      {onManage && (
        <>
          <div className="bg-border my-1 h-px" role="separator" />
          <button
            className="hover:bg-accent text-muted-foreground hover:text-foreground flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-sm"
            data-testid="plugins-menu-manage"
            type="button"
            onClick={onManage}
          >
            <Settings2 className="size-4" />
            <span className="min-w-0 flex-1 truncate">
              {t("questioninput.plugins_manage", { defaultValue: "Manage plugins…" })}
            </span>
          </button>
        </>
      )}
    </div>
  )
}
